import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  LogOut,
  User,
  ShoppingCart,
  TrendingUp,
  Package,
  Layers,
  BarChart3,
  LayoutGrid
} from 'lucide-react';

// Configuración visual por área
const AREA_CONFIG = {
  compras: {
    label: 'Compras',
    icon: ShoppingCart,
    activeClass: 'bg-blue-50 text-blue-700 border-blue-200',
    dotClass: 'bg-blue-500',
    indicatorClass: 'bg-blue-500',
    iconClass: 'text-blue-500',
  },
  ventas: {
    label: 'Ventas',
    icon: TrendingUp,
    activeClass: 'bg-green-50 text-green-700 border-green-200',
    dotClass: 'bg-green-500',
    indicatorClass: 'bg-green-500',
    iconClass: 'text-green-500',
  },
  stock: {
    label: 'Stock',
    icon: Package,
    activeClass: 'bg-orange-50 text-orange-700 border-orange-200',
    dotClass: 'bg-orange-500',
    indicatorClass: 'bg-orange-500',
    iconClass: 'text-orange-500',
  },
  general: {
    label: 'General',
    icon: Layers,
    activeClass: 'bg-gray-100 text-gray-700 border-gray-200',
    dotClass: 'bg-gray-400',
    indicatorClass: 'bg-gray-400',
    iconClass: 'text-gray-500',
  },
};

const getAreaConfig = (area) =>
  AREA_CONFIG[area?.toLowerCase()] || {
    label: area ? area.charAt(0).toUpperCase() + area.slice(1) : 'General',
    icon: BarChart3,
    activeClass: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    dotClass: 'bg-[#00B7D4]',
    indicatorClass: 'bg-[#00B7D4]',
    iconClass: 'text-[#00B7D4]',
  };

const Sidebar = ({ quickActions = [], selectedArea, onAreaSelect, onSelectConversation }) => {
  const { messages, startNewChat } = useChat();
  const { user, logout, paneles } = useAuth();
  const navigate = useNavigate();

  const handleNewChat = () => {
    startNewChat();
    onSelectConversation?.();
  };

  // Derivar lista de áreas únicas respetando el orden de aparición en quickActions
  const areas = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const action of quickActions) {
      const area = action.area || 'general';
      if (!seen.has(area)) {
        seen.add(area);
        result.push(area);
      }
    }
    return result;
  }, [quickActions]);

  // Conteo de acciones por área para el badge
  const countByArea = useMemo(() => {
    return quickActions.reduce((acc, action) => {
      const area = action.area || 'general';
      acc[area] = (acc[area] || 0) + 1;
      return acc;
    }, {});
  }, [quickActions]);

  return (
    <aside className="w-64 h-screen border-r bg-white flex flex-col" data-testid="sidebar">
      {/* Header con Logo */}
      <div className="p-4 border-b brand-header">
        <div className="flex items-center gap-3 mb-4">
          <img src="/logo-cdsur.png" alt="CDSur" className="h-10 w-auto" />
        </div>
        <p className="text-xs text-muted-foreground mb-3">Diagnóstico Operativo</p>
        {messages.length > 0 && (
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            onClick={handleNewChat}
            data-testid="new-chat-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a las acciones
          </Button>
        )}
      </div>

      {/* Módulos / Áreas */}
      <nav className="flex-1 overflow-y-auto p-3">
        <p className="px-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-3">
          Módulos
        </p>

        {areas.length === 0 ? (
          <p className="px-2 text-xs text-muted-foreground italic">Sin módulos disponibles</p>
        ) : (
          <div className="space-y-1">
            {areas.map((area) => {
              const cfg = getAreaConfig(area);
              const Icon = cfg.icon;
              const isActive = selectedArea === area;
              const count = countByArea[area] || 0;

              return (
                <button
                  key={area}
                  onClick={() => onAreaSelect?.(area)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left
                    transition-all duration-200 relative group border
                    ${isActive
                      ? `${cfg.activeClass} shadow-sm`
                      : 'text-gray-500 border-transparent hover:bg-gray-50 hover:text-gray-800'
                    }
                  `}
                >
                  {/* Indicador lateral activo */}
                  {isActive && (
                    <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full ${cfg.indicatorClass}`} />
                  )}

                  <Icon className={`h-4 w-4 shrink-0 ${isActive ? cfg.iconClass : 'text-gray-400 group-hover:text-gray-600'}`} />

                  <span className="flex-1 text-sm font-semibold">{cfg.label}</span>

                  {/* Badge con cantidad de acciones */}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center
                    ${isActive ? `${cfg.dotClass} text-white` : 'bg-gray-100 text-gray-500'}
                  `}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="p-2 border-t space-y-1">
        <div className="flex items-center gap-2 px-2 py-2">
          <div className="h-8 w-8 rounded-full bg-[#00B7D4] flex items-center justify-center shrink-0">
            <User className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
          </div>
        </div>

        {/* Solo tiene sentido si hay más de un panel al que volver. */}
        {paneles.length > 1 && (
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 text-muted-foreground hover:text-[#00B7D4]"
            onClick={() => navigate('/')}
            data-testid="volver-inicio-btn"
          >
            <LayoutGrid className="h-4 w-4" />
            Volver al inicio
          </Button>
        )}

        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-muted-foreground hover:text-destructive"
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4" />
          Cerrar sesión
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
